import React, {
    useState,
    useEffect
} from "react";

import axios from "axios";

import "./ProfileHero.css";

import {
    Camera,
    Edit3,
    Mail,
    Phone,
    Award
} from "lucide-react";

import EditProfileModal
    from "../Components/EditProfileModal";


const ProfileHero = ({
    data,
    profilePhoto,
    refreshProfile
}) => {


    /* =====================================================
       USER
    ===================================================== */

    const user =
        data?.user ||
        data?.profile ||
        data ||
        {};


    /* =====================================================
       SUMMARY
    ===================================================== */

    const summary =
        data?.summary ||
        {};


    /* =====================================================
       PHOTO STATE
    ===================================================== */

    const [
        photo,
        setPhoto
    ] = useState(

        profilePhoto ||
        user?.profilePhoto ||
        localStorage.getItem(
            "profile_photo"
        ) ||
        ""

    );


    /* =====================================================
       MODAL
    ===================================================== */

    const [
        showModal,
        setShowModal
    ] = useState(false);


    /* =====================================================
       UPLOADING
    ===================================================== */

    const [
        uploading,
        setUploading
    ] = useState(false);


    /* =====================================================
       UPDATE PHOTO WHEN PARENT CHANGES
    ===================================================== */

    useEffect(() => {

        const newPhoto =
            profilePhoto ||
            user?.profilePhoto ||
            localStorage.getItem(
                "profile_photo"
            ) ||
            "";


        if (newPhoto) {

            setPhoto(
                newPhoto
            );

        }

    }, [
        profilePhoto,
        user?.profilePhoto
    ]);


    /* =====================================================
       UPLOAD PHOTO
    ===================================================== */

    const handlePhotoUpload =
        async (event) => {

            try {

                const file =
                    event.target.files?.[0];


                if (!file) {

                    return;

                }


                if (
                    !file.type.startsWith(
                        "image/"
                    )
                ) {

                    alert(
                        "Please select an image."
                    );

                    return;

                }


                setUploading(
                    true
                );


                /* =========================================
                   FORM DATA
                ========================================= */

                const formData =
                    new FormData();


                formData.append(
                    "photo",
                    file
                );


                formData.append(
                    "email",
                    user.email
                );


                console.log(
                    "Uploading photo:",
                    user.email
                );


                /* =========================================
                   UPLOAD
                ========================================= */

                const response =
                    await axios.post(

                        "/api/upload-profile-photo",

                        formData,

                        {
                            headers: {
                                "Content-Type":
                                    "multipart/form-data"
                            }
                        }

                    );


                console.log(
                    "UPLOAD RESPONSE:",
                    response.data
                );


                /* =========================================
                   PHOTO URL
                ========================================= */

                const uploadedPhoto =
                    response.data?.photoUrl;


                if (!uploadedPhoto) {

                    alert(
                        "Photo URL not returned from server."
                    );

                    return;

                }


                console.log(
                    "UPLOADED PHOTO URL:",
                    uploadedPhoto
                );


                /* =========================================
                   UPDATE UI IMMEDIATELY
                ========================================= */

                setPhoto(
                    uploadedPhoto
                );


                /* =========================================
                   LOCAL STORAGE
                ========================================= */

                localStorage.setItem(
                    "profile_photo",
                    uploadedPhoto
                );


                /* =========================================
                   NAVBAR / HEADER EVENT
                ========================================= */

                window.dispatchEvent(

                    new CustomEvent(
                        "profileUpdated",
                        {
                            detail: {
                                photo:
                                    uploadedPhoto
                            }
                        }
                    )

                );


                /* =========================================
                   REFRESH PROFILE
                ========================================= */

                if (
                    refreshProfile
                ) {

                    await refreshProfile();

                }


            }

            catch (error) {

                console.error(
                    "Profile Photo Upload Error:",
                    error
                );


                console.error(
                    "SERVER ERROR:",
                    error.response?.data
                );


                alert(
                    error.response?.data?.error ||
                    "Failed to upload photo."
                );

            }

            finally {

                setUploading(
                    false
                );


                event.target.value =
                    "";

            }

        };


    /* =====================================================
       FINAL PHOTO
    ===================================================== */

    const finalPhoto =
        photo ||
        user?.profilePhoto ||
        localStorage.getItem(
            "profile_photo"
        ) ||
        "";


    console.log(
        "PROFILE HERO FINAL PHOTO:",
        finalPhoto
    );


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <>

            <div className="profile-hero">


                {/* =================================================
                    LEFT
                ================================================= */}

                <div className="profile-left">


                    {/* =================================================
                        PHOTO
                    ================================================= */}

                    <div className="profile-photo-wrapper">


                        {

                            finalPhoto ? (

                                <img

                                    src={
                                        finalPhoto
                                    }

                                    alt="Profile"

                                    className="profile-photo"

                                    onError={(event) => {

                                        console.error(
                                            "IMAGE LOAD FAILED:",
                                            finalPhoto
                                        );

                                        event.currentTarget.src =
                                            "/Hero/profile.png";

                                    }}

                                />

                            ) : (

                                <img

                                    src="/Hero/profile.png"

                                    alt="Default Profile"

                                    className="profile-photo"

                                />

                            )

                        }


                        {/* =============================================
                            CAMERA
                        ============================================= */}

                        <label
                            className="photo-upload-btn"
                        >

                            <Camera
                                size={18}
                            />


                            <input

                                type="file"

                                accept="image/*"

                                hidden

                                disabled={
                                    uploading
                                }

                                onChange={
                                    handlePhotoUpload
                                }

                            />

                        </label>


                        {

                            uploading && (

                                <div className="photo-upload-loading">

                                    Uploading...

                                </div>

                            )

                        }

                    </div>


                    {/* =================================================
                        USER DETAILS
                    ================================================= */}

                    <div className="profile-details">


                        <h2>

                            {
                                user.name ||
                                "User"
                            }

                        </h2>


                        <p>

                            <Mail
                                size={16}
                            />

                            {
                                user.email
                            }

                        </p>


                        <p>

                            <Phone
                                size={16}
                            />

                            {
                                user.phone ||
                                "Not Available"
                            }

                        </p>

                    </div>

                </div>


                {/* =================================================
                    RIGHT
                ================================================= */}

                <div className="profile-right">


                    <div className="level-card">

                        <Award
                            size={24}
                        />

                        <div>

                            <span>
                                Current Level
                            </span>

                            <h3>

                                {
                                    summary.currentLevel ||
                                    user.level ||
                                    1
                                }

                            </h3>

                        </div>

                    </div>


                    <div className="xp-card">

                        <span>
                            Total XP
                        </span>

                        <h2>

                            {
                                summary.totalXP ||
                                0
                            }

                        </h2>

                    </div>


                    <button

                        className="edit-profile-btn"

                        onClick={() =>
                            setShowModal(true)
                        }

                    >

                        <Edit3
                            size={18}
                        />

                        Edit Profile

                    </button>

                </div>

            </div>


            {/* =====================================================
                EDIT MODAL
            ===================================================== */}

            {

                showModal && (

                    <EditProfileModal

                        user={user}

                        onClose={() =>
                            setShowModal(false)
                        }

                        refreshProfile={
                            refreshProfile
                        }

                    />

                )

            }

        </>

    );

};


export default ProfileHero;